import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, App as AntApp, theme as antdTheme } from 'antd';
import { ThemeProvider } from 'styled-components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import useThemeStore from '@stores/theme.store';
import { getTheme, getAntdTheme } from '@theme/theme';
import MessageProvider from '@context/MessageProvider';
import NotificationProvider from '@context/NotificationProvider';
import { initializeHttpInterceptors } from '@utils/http';
import { useAuthStore } from '@stores/auth.store';
import Outside from '@layout/Outside';
import Signin from '@pages/outside/Signin';
import ForgotPassword from '@pages/outside/ForgotPassword';
import AppHome from '@pages/AppHome';

initializeHttpInterceptors();

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: 1
        }
    }
});

function RequireAuth({ children }) {
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    if (!isAuthenticated) {
        return <Navigate to="/sign-in" replace />;
    }
    return children;
}

function AppShell() {
    const mode = useThemeStore((state) => state.mode);
    const currentTheme = getTheme(mode);
    const antdThemeConfig = getAntdTheme(mode);
    const algorithm =
        mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm;

    const processComponents = (components) => {
        const processed = {};
        Object.keys(components).forEach((componentName) => {
            const config = components[componentName];
            processed[componentName] = {
                ...config,
                fontFamily: config.fontFamily || currentTheme.fonts.family
            };
        });
        return processed;
    };

    const finalAntdConfig = {
        ...antdThemeConfig,
        algorithm,
        components: {
            ...processComponents(antdThemeConfig.components),
            Message: {
                duration: 6
            }
        }
    };

    useEffect(() => {
        const themeColor = document.querySelector('meta[name="theme-color"]');
        if (themeColor) {
            themeColor.content = mode === 'dark' ? '#141414' : '#ffffff';
        }
    }, [mode]);

    useEffect(() => {
        useAuthStore.setState({ isRefreshing: false });
    }, []);

    return (
        <ConfigProvider theme={finalAntdConfig}>
            <QueryClientProvider client={queryClient}>
                <ThemeProvider theme={currentTheme}>
                    <AntApp>
                        <MessageProvider>
                            <NotificationProvider>
                                <Routes>
                                    <Route
                                        path="/"
                                        element={
                                            <Outside>
                                                <Signin />
                                            </Outside>
                                        }
                                    />
                                    {/* Public eDoc entry (live website): /:edocNumber */}
                                    <Route
                                        path="/:edocNumber"
                                        element={<Signin />}
                                    />
                                    <Route
                                        path="/sign-in"
                                        element={
                                            <Outside>
                                                <Signin />
                                            </Outside>
                                        }
                                    />
                                    <Route
                                        path="/sign-in/:edocNumber"
                                        element={<Navigate to="/:edocNumber" replace />}
                                    />
                                    <Route
                                        path="/forgot-password"
                                        element={
                                            <Outside>
                                                <ForgotPassword />
                                            </Outside>
                                        }
                                    />
                                    <Route
                                        path="/app"
                                        element={
                                            <RequireAuth>
                                                <AppHome />
                                            </RequireAuth>
                                        }
                                    />
                                    <Route path="*" element={<Navigate to="/" replace />} />
                                </Routes>
                            </NotificationProvider>
                        </MessageProvider>
                    </AntApp>
                </ThemeProvider>
            </QueryClientProvider>
        </ConfigProvider>
    );
}

export default function App() {
    return (
        <BrowserRouter>
            <AppShell />
        </BrowserRouter>
    );
}
